import type { VaultFileEntry } from './types.js';
import { basename, dirname, joinPath, normalizePath, titleFromPath } from './paths.js';

/**
 * Obsidian "shortest path when possible" link resolution.
 *
 * Rules (matching Obsidian's default):
 *  1. If the target contains '/', it is resolved as a vault path
 *     (case-insensitive, .md optional), first absolute, then relative to the
 *     source note's folder.
 *  2. Otherwise the target matches any file whose basename (without .md)
 *     equals it case-insensitively; when ambiguous the one with the shortest
 *     full path wins (ties: lexicographic).
 *  3. Frontmatter aliases resolve the same way, after name matches.
 *  4. No match -> null (an "unresolved"/phantom link).
 */
export class ResolveIndex {
  private byPath = new Map<string, string>(); // lower path -> id
  private idToPath = new Map<string, string>();
  private byName = new Map<string, { id: string; path: string }[]>();
  private byAlias = new Map<string, { id: string; path: string }[]>();

  constructor(
    files: Iterable<[string, VaultFileEntry]>,
    aliases?: Map<string, string[]>, // id -> aliases
  ) {
    for (const [id, f] of files) {
      if (f.deleted) continue;
      const lower = f.path.toLowerCase();
      this.byPath.set(lower, id);
      this.idToPath.set(id, f.path);
      if (lower.endsWith('.md')) this.byPath.set(lower.slice(0, -3), id);
      const name = titleFromPath(f.path).toLowerCase();
      push(this.byName, name, { id, path: f.path });
      // full basename with extension also matches (attachments: [[img.png]])
      const base = basename(f.path).toLowerCase();
      if (base !== name) push(this.byName, base, { id, path: f.path });
    }
    if (aliases) {
      for (const [id, list] of aliases) {
        const p = this.idToPath.get(id);
        if (p == null) continue;
        for (const a of list) push(this.byAlias, a.toLowerCase(), { id, path: p });
      }
    }
    for (const m of [this.byName, this.byAlias]) {
      for (const list of m.values()) {
        list.sort((a, b) => a.path.length - b.path.length || (a.path < b.path ? -1 : 1));
      }
    }
  }

  pathFor(id: string): string | undefined {
    return this.idToPath.get(id);
  }

  /** Resolve a wikilink target from a note at `fromPath`. Returns file id or null. */
  resolve(target: string, fromPath: string): string | null {
    const t = normalizePath(target.trim());
    if (!t) return null;
    const lower = t.toLowerCase();
    if (t.includes('/')) {
      const abs = this.byPath.get(lower);
      if (abs) return abs;
      const rel = this.byPath.get(joinPath(dirname(fromPath), t).toLowerCase());
      if (rel) return rel;
    }
    const name = this.byName.get(lower);
    if (name && name.length > 0) return name[0].id;
    const alias = this.byAlias.get(lower);
    if (alias && alias.length > 0) return alias[0].id;
    // paths given without folder still match rule 1 via byPath (root files)
    const asPath = this.byPath.get(lower);
    if (asPath) return asPath;
    return null;
  }
}

function push<K, V>(m: Map<K, V[]>, k: K, v: V) {
  const list = m.get(k);
  if (list) list.push(v);
  else m.set(k, [v]);
}

/**
 * The shortest unambiguous link text for a file — used when generating links
 * (autocomplete, rename rewriting): bare name when unique, else full path.
 */
export function shortestLinkText(
  path: string,
  files: Iterable<VaultFileEntry>,
): string {
  const name = titleFromPath(path);
  let count = 0;
  for (const f of files) {
    if (f.deleted) continue;
    if (titleFromPath(f.path).toLowerCase() === name.toLowerCase()) count++;
  }
  if (count <= 1) return name;
  return path.endsWith('.md') ? path.slice(0, -3) : path;
}
